import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { Address } from "@redotech/redo-model/draft-return/draft-return";
import { Adjustment } from "@redotech/redo-model/return-flow";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { CollectionProductInfo } from "@redotech/shopify-storefront";
import { ExchangeProduct } from "../create-return/components/variant-bubbles";

export function adjustmentName(
  adjustment: ReturnAppSettings["returnAdjustment"],
  value: number,
): string {
  return adjustment[value > 0 ? "positiveName" : "negativeName"];
}

export function getShowAdjustment(adjustment: Adjustment): boolean {
  return adjustment.flat !== 0 || adjustment.proportion !== 1;
}

export function getAdjustmentValue(
  adjustment: Adjustment,
  discountPrice: string,
): number {
  const adjustmentValue =
    parseFloat(discountPrice) * (adjustment.proportion - 1.0) + adjustment.flat;

  return adjustmentValue;
}

export function isAddressComplete(
  address: Address | undefined | null,
): boolean {
  return (
    !!address &&
    !!address.street1 &&
    !!address.city &&
    // state is not required for international addresses
    !!address.zip
  );
}

export function subInLineBreaks(text: string): JSX.Element {
  return (
    <>
      {text.split("\n").map((str, i) => (
        <div key={i}>{str}</div>
      ))}
    </>
  );
}

export const exchangeGroupFilter = (
  product: ExchangeProduct,
  exchangeGroup: ExchangeGroup,
): boolean => {
  if (!exchangeGroup) {
    return true;
  }
  switch (exchangeGroup.type) {
    case "tag:tag":
      return product.tags.some((tag: string) =>
        exchangeGroup.targetTags.includes(tag),
      );
    case "tag:collection":
    case "name:collection":
    case "collection:collection":
      return product.collectionIds.includes(exchangeGroup.targetCollection.id);
  }
  return true;
};

export const excludeVariantExchangeStoreUrls = [
  "hotleathers.myshopify.com",
  "leather-up-us.myshopify.com",
  "milwaukee-motorcycle-clothing-co.myshopify.com",
];

export const getProductInfo = (
  productData: CollectionProductInfo,
): ExchangeProduct => {
  try {
    return {
      id: productData.id.split("/").pop() ?? "",
      title: productData.title,
      vendor: productData.vendor,
      productType: productData.productType,
      availableForSale: productData.availableForSale,
      totalInventory: productData?.totalInventory ?? 0,
      tags: productData.tags,
      image: productData.featuredImage?.url ?? "",
      price: productData.priceRange.minVariantPrice.amount,
      publishedAt: productData.publishedAt,
      description: productData.description,
      variants: productData.variants.nodes.map((variant) => ({
        title: variant.title,
        productTitle: productData.title,
        productId: productData.id,
        availableForSale: variant.availableForSale,
        id: variant.id.split("/").pop() ?? "",
        price: variant.price.amount,
        option1: variant.selectedOptions?.[0]?.value ?? null,
        option2: variant.selectedOptions?.[1]?.value ?? null,
        option3: variant.selectedOptions?.[2]?.value ?? null,
        quantityAvaliable: variant?.quantityAvailable ?? 0,
        currentlyNotInStock: variant.currentlyNotInStock,
        selectedOptions: variant.selectedOptions,
        image: variant?.image?.url ?? null,
        requiresShipping: variant.requiresShipping,
        sku: variant?.sku ?? null,
      })),
      options: productData.options,
      collectionIds: productData.collections.nodes.reduce(
        (acc: string[], collection) => {
          const id = collection.id.split("/").pop() ?? "";
          if (id) {
            acc.push(id);
          }
          return acc;
        },
        [],
      ),
    };
  } catch (e) {
    console.error(e);
    throw e;
  }
};
